/* CS 1.6 overlay — leest events die door de RCON-poller worden gepusht.
   Verwachte events (zie backend/src/services/cs16Overlay*.js):
   - "status": { hostname, map, ct_score, t_score, players: [{ name, team, frags, deaths, ping }] }

   Overlay-context (F1) — als kanaal aan een tournament_match hangt:
   - context.team_a, context.team_b: { name, logo }
   - context.channel.sides_swapped: bool (bij halftime toggelt admin dit)
   - context.tournament: { name }, context.match: { round, match_number }

   Team-namen blijven op hun kant staan; bij side-swap wisselen team_a↔team_b
   ten opzichte van de CT/T strips. */

import { useEffect, useRef, useState } from 'react'

export default function CS16OverlayVariant({ byType, context }) {
  // Data-bronnen, in volgorde van voorkeur:
  // 1) amx_status  → BB_OVERLAY AMXX-plugin (team + deaths + accurate scores)
  // 2) team_score  → HLDS-log events (echte round-eind scores)
  // 3) status      → RCON `status` (fallback: alleen naam + frags + ping)
  const amx = byType?.amx_status?.data
  const rcon = byType?.status?.data
  const tsEvent = byType?.team_score?.data
  const killEvent = byType?.kill

  // K/D-delta live via UDP kill-events. amx_status is de baseline (elke
  // 15s via poll of instant via UDP als bb_overlay dat push't); tussen
  // updates in tellen we kills/deaths bij vanuit real-time kill-events
  // en resetten de delta zodra een verse amx_status binnenkomt.
  const [delta, setDelta] = useState(() => ({ /* name -> {frags, deaths} */ }))
  const amxTsRef  = useRef(amx ? byType.amx_status.ts : 0)
  const lastKillTsRef = useRef(0)
  useEffect(() => {
    const amxTs = byType?.amx_status?.ts || 0
    if (amxTs && amxTs !== amxTsRef.current) {
      amxTsRef.current = amxTs
      setDelta({})   // baseline vernieuwd → delta resetten
    }
  }, [byType?.amx_status?.ts])
  useEffect(() => {
    if (!killEvent) return
    const kt = killEvent.ts || 0
    if (kt <= lastKillTsRef.current) return
    lastKillTsRef.current = kt
    const k = killEvent.data
    if (!k?.killer || !k?.victim) return
    setDelta(prev => {
      const nw = { ...prev }
      // Suicide/team-kill: alleen deaths tellen
      if (k.killer !== k.victim) {
        nw[k.killer] = { frags: (nw[k.killer]?.frags || 0) + 1, deaths: nw[k.killer]?.deaths || 0 }
      }
      nw[k.victim] = { frags: nw[k.victim]?.frags || 0, deaths: (nw[k.victim]?.deaths || 0) + 1 }
      return nw
    })
  }, [killEvent])

  // Kies primaire bron voor spelers-lijst + scores.
  // Scores worden overschreven door team_score-events uit de HL-log als
  // die aanwezig zijn — dat is de meest accurate bron op ReGameDLL (waar
  // mp_ct_wins/mp_t_wins niet altijd betrouwbaar zijn).
  const status = amx || rcon
  const scoreCT = tsEvent?.ct_score ?? status?.ct_score ?? 0
  const scoreT  = tsEvent?.t_score  ?? status?.t_score  ?? 0

  const swapped = !!context?.channel?.sides_swapped
  const ctTeam = swapped ? context?.team_b : context?.team_a
  const tTeam  = swapped ? context?.team_a : context?.team_b

  if (!status) {
    return (
      <>
        {context?.tournament && <ContextHeader context={context} />}
        <div style={waiting}>
          <div style={label}>CS 1.6 · wacht op RCON-data…</div>
          {ctTeam?.name && tTeam?.name && (
            <div style={{ ...label, marginTop: 6, color: '#fff' }}>
              {ctTeam.name} <span style={{ color: '#94A3B8' }}>vs</span> {tTeam.name}
            </div>
          )}
        </div>
      </>
    )
  }
  const basePlayers = Array.isArray(status.players) ? status.players : []
  const players = basePlayers.map(p => {
    const d = delta[p.name]
    if (!d) return p
    return { ...p, frags: (p.frags || 0) + d.frags, deaths: (p.deaths || 0) + d.deaths }
  })
  const ct = players.filter(p => p.team === 'CT').sort(byFrags)
  const t  = players.filter(p => p.team === 'TERRORIST' || p.team === 'T').sort(byFrags)
  return (
    <>
      {context?.tournament && <ContextHeader context={context} />}
      {/* Top scoreboard */}
      <div style={topBar}>
        <TeamScore label="CT" teamName={ctTeam?.name} score={scoreCT} color="#61B4E2" />
        <div style={mapPill}>{status.map || '—'}</div>
        <TeamScore label="T" teamName={tTeam?.name} score={scoreT} color="#E2A961" />
      </div>
      {/* Left CT strip */}
      <div style={{ ...sideStrip, left: 24 }}>
        <StripHeader color="#61B4E2" side="CT" teamName={ctTeam?.name} />
        {ct.map(p => <PlayerRow key={p.name} p={p} tone="#61B4E2" />)}
      </div>
      {/* Right T strip */}
      <div style={{ ...sideStrip, right: 24 }}>
        <StripHeader color="#E2A961" side="T" teamName={tTeam?.name} />
        {t.map(p => <PlayerRow key={p.name} p={p} tone="#E2A961" />)}
      </div>
    </>
  )
}

/* Vaste bar bovenaan met "Belgian Bears vs KVK · R2·M1 · TournooiNaam" */
function ContextHeader({ context }) {
  const t = context.tournament
  const m = context.match
  const a = context.team_a?.name || 'Team A'
  const b = context.team_b?.name || 'Team B'
  return (
    <div style={ctxHeader}>
      <span style={ctxTitle}>{a} <span style={{ color: '#94A3B8' }}>vs</span> {b}</span>
      <span style={ctxMeta}>
        {t?.name}{m?.round ? ` · R${m.round}·M${m.match_number}` : ''}
      </span>
    </div>
  )
}

function TeamScore({ label, teamName, score, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
      <div style={{ fontFamily: 'monospace', fontSize: 11, letterSpacing: '.2em', color }}>{label}</div>
      <div style={{ fontFamily: 'Rajdhani, system-ui, sans-serif', fontSize: 44, fontWeight: 700, lineHeight: 1, color }}>
        {score}
      </div>
      {teamName && (
        <div style={{
          fontFamily: 'system-ui, sans-serif', fontSize: 11, fontWeight: 600, color: '#fff',
          marginTop: 4, textAlign: 'center', maxWidth: 160,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{teamName}</div>
      )}
    </div>
  )
}

function StripHeader({ children, color, side, teamName }) {
  const text = teamName
    ? `${teamName} · ${side === 'CT' ? 'Counter-Terrorists' : 'Terrorists'}`
    : (children || (side === 'CT' ? 'Counter-Terrorists' : 'Terrorists'))
  return (
    <div style={{
      fontFamily: 'monospace', fontSize: 10, letterSpacing: '.2em',
      textTransform: 'uppercase', color, opacity: .9, marginBottom: 4,
      padding: '4px 8px', borderBottom: `1px solid ${color}66`,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>{text}</div>
  )
}

function PlayerRow({ p, tone }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto auto',
      alignItems: 'center', gap: 8, padding: '4px 8px',
      background: 'rgba(0,0,0,.55)', borderLeft: `2px solid ${tone}`,
      fontFamily: 'system-ui, sans-serif', fontSize: 13, color: '#fff',
    }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
      <span style={{ fontFamily: 'monospace', fontSize: 12, color: tone }}>{p.frags}</span>
      <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#999' }}>·{p.deaths}</span>
    </div>
  )
}

const byFrags = (a, b) => (b.frags || 0) - (a.frags || 0)
const label = { fontFamily: 'monospace', fontSize: 12, letterSpacing: '.2em',
                textTransform: 'uppercase', color: '#B4FF39', opacity: .7 }
const waiting = { position: 'fixed', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  padding: '16px 24px', background: 'rgba(0,0,0,.6)', borderRadius: 8,
                  textAlign: 'center' }
const topBar = {
  position: 'fixed', top: 68, left: '50%', transform: 'translateX(-50%)',
  display: 'flex', alignItems: 'center', gap: 24,
  padding: '12px 28px', background: 'rgba(10,10,10,.78)',
  border: '1px solid rgba(255,255,255,.08)', borderRadius: 8,
  backdropFilter: 'blur(4px)',
}
const mapPill = {
  fontFamily: 'monospace', fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase',
  padding: '4px 10px', background: 'rgba(255,255,255,.06)', borderRadius: 4, color: '#B4FF39',
}
const sideStrip = {
  position: 'fixed', top: '22%', width: 260,
  display: 'flex', flexDirection: 'column', gap: 2,
}
const ctxHeader = {
  position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '6px 16px', background: 'rgba(0,0,0,.72)',
  border: '1px solid rgba(180,255,57,.28)', borderRadius: 6,
  backdropFilter: 'blur(4px)',
}
const ctxTitle = {
  fontFamily: 'system-ui, sans-serif', fontSize: 14, fontWeight: 700, color: '#fff',
}
const ctxMeta = {
  fontFamily: 'monospace', fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase',
  color: '#B4FF39',
}
const killWrap = {
  // Rechtsboven, net onder de context-header — moet niet botsen met
  // scoreboard (dat is midden bovenaan) noch met T-strip (start op ~22%).
  position: 'fixed', top: 56, right: 24,
  display: 'flex', flexDirection: 'column', gap: 3,
  fontFamily: 'system-ui, sans-serif',
  pointerEvents: 'none',
  maxWidth: 380,
}
const killRow = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '4px 10px', fontSize: 13, color: '#fff',
  background: 'rgba(0,0,0,.7)', borderRadius: 3,
  backdropFilter: 'blur(3px)',
}
const killName = { fontWeight: 700, maxWidth: 140,
                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
const weaponPill = {
  fontFamily: 'monospace', fontSize: 10, letterSpacing: '.1em',
  textTransform: 'lowercase', color: '#ccc',
  padding: '1px 6px', background: 'rgba(255,255,255,.06)', borderRadius: 3,
}
